import Cookies from 'js-cookie';

import { fromBase64, toBase64 } from '@change-corgi/core/base64';
import { getWindow } from '@change-corgi/core/window';

/**
 * add useful functions to the window object
 *
 * @example
 * window._exp.all()
 * window._exp.get(experimentId)
 * window._exp.set(experimentId, variation)
 * window._exp.set(experimentId, variation, config) // e.g. forcing a growthbook/amplitude payload
 * window._exp.unset(experimentId)
 * window._exp.clear()
 */
export function decorateWindowForExperiments(): void {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const window: any = getWindow();
	if (!window) {
		return;
	}
	const facade = getFacade();
	// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
	window._sov = (id: string, value: string | undefined) => {
		// eslint-disable-next-line no-console
		console.warn('_sov is deprecated, please use _exp instead');
		return facade.set(id, value);
	};
	// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
	window._exp = facade;
}

type Facade = {
	all: () => Readonly<Record<string, string>>;
	get: (id?: string) => string | undefined;
	set: (id: string, value: string | undefined, config?: unknown) => Facade;
	unset: (id: string) => Facade;
	clear: () => Facade;
};

function getFacade() {
	const facade: Facade = {
		all: () => getExperimentOverrides(),
		get: (id?: string) => {
			if (!id) {
				throw new Error('parameter missing');
			}
			return getExperimentOverride(id);
		},
		// chainable
		set: (id: string, value: string | undefined, config?: unknown): Facade => {
			if (!id) {
				throw new Error('parameter missing');
			}
			setExperimentOverride(id, value, config);
			return facade;
		},
		// chainable
		unset: (id: string): Facade => {
			if (!id) {
				throw new Error('parameter missing');
			}
			unsetExperimentOverride(id);
			return facade;
		},
		// chainable
		clear: (): Facade => {
			getExperimentOverridesNames().forEach((id) => unsetExperimentOverride(id));
			return facade;
		},
	};
	return facade;
}

function getExperimentOverridesNames(): readonly string[] {
	const cookieNames = Object.keys(Cookies.get())
		.map((key) => (/^Experiments_(.*)$/.exec(key) || [])[1])
		.filter((name) => !!name);
	return cookieNames;
}

function getExperimentOverride(name: string): string | undefined {
	const rawValue = Cookies.get(`Experiments_${name}`);
	return rawValue === undefined ? undefined : decodeOverrideValueForDisplay(rawValue);
}

function getExperimentOverrides(): Readonly<Record<string, string>> {
	return getExperimentOverridesNames().reduce<Record<string, string>>((acc, id) => {
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion, no-param-reassign
		acc[id] = getExperimentOverride(id)!;
		return acc;
	}, {});
}

/**
 * The cookie stores `variation:<base64(json)>` for a forced config; decode it back to
 * `variation:<json>` so the console shows something readable. Falls back to the raw value when
 * there's no `:` or the tail doesn't decode.
 */
function decodeOverrideValueForDisplay(rawValue: string): string {
	const colonIndex = rawValue.indexOf(':');
	if (colonIndex === -1) {
		return rawValue;
	}
	try {
		const config = JSON.parse(fromBase64(rawValue.slice(colonIndex + 1))) as unknown;
		return `${rawValue.slice(0, colonIndex)}:${JSON.stringify(config)}`;
	} catch {
		return rawValue;
	}
}

function setExperimentOverride(name: string, variation: string | undefined, config?: unknown) {
	const cookieName = `Experiments_${name}`;
	if (!variation) {
		Cookies.remove(cookieName);
	} else {
		const value = config === undefined ? variation : `${variation}:${toBase64(JSON.stringify(config))}`;
		Cookies.set(cookieName, value);
	}
}

function unsetExperimentOverride(name: string) {
	setExperimentOverride(name, undefined);
}
